// @flow

import { isNumber, negate } from 'lodash';

// This is by no means foolproof, but we don't want to send server noise. Let's
// assume that certain status codes are due to:
// - external api problems
// - server hiccups
// - user solvable issues that will be seen in error bars/modals
type ErrorStatusCode = number;

const isExpectedStatus = (status: ErrorStatusCode) => {
  // A 0 status code is a placeholder for when there is no status code, for
  // example a connection error or timeout. We can't do a whole lot about these
  // on the client, so we ignore them. See http://bit.ly/2BwlMpq
  if (status === 0) {
    return true;
  }
  // If we made a bad request, we should know about that.
  if (status === 400) {
    return false;
  }
  // Other 400 errors and 500 errors are probably not something the frontend
  // can handle.
  if (status > 400) {
    return true;
  }
  // Otherwise, we're not expecting this.
  return false;
};

type NetworkError = {
  isNetworkError?: boolean,
  status?: ?ErrorStatusCode,
  statusCode?: ?ErrorStatusCode,
};

export const shouldTrackNetworkError = (error: ?NetworkError) => {
  if (!error) {
    return false;
  }
  const isConnectionError = error.isNetworkError;
  if (isConnectionError) {
    return false;
  }
  // This handles the ridiculousness of `0` vs `undefined` vs
  // `null` and how `isNaN` treats those.
  const status = isNumber(error.status)
    ? error.status
    : isNumber(error.statusCode)
    ? error.statusCode
    : undefined;
  return status !== undefined && !isExpectedStatus(status);
};

export const shouldIgnoreNetworkError = negate(shouldTrackNetworkError);
